import { useState, useRef, useCallback, useEffect } from "react";

export interface TapEditBlockProps {
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  rows?: number;
  maxLength?: number;
  disabled?: boolean;
  label?: string;
}

export function TapEditBlock({
  value,
  onChange,
  placeholder,
  rows = 3,
  maxLength,
  disabled = false,
  label,
}: TapEditBlockProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const displayRef = useRef<HTMLDivElement>(null);
  const cancelledRef = useRef(false);

  useEffect(() => {
    if (editing && textareaRef.current) {
      textareaRef.current.focus();
      textareaRef.current.selectionStart = value.length;
      textareaRef.current.selectionEnd = value.length;
    }
  }, [editing, value]);

  useEffect(() => {
    if (!editing && cancelledRef.current && displayRef.current) {
      displayRef.current.focus();
      cancelledRef.current = false;
    }
  }, [editing]);

  const commit = useCallback(
    (draftValue: string) => {
      const next = draftValue.trim();
      setEditing(false);
      setDraft("");
      onChange(next);
    },
    [onChange],
  );

  const cancel = useCallback(() => {
    setEditing(false);
    setDraft("");
  }, []);

  const startEdit = useCallback(() => {
    cancelledRef.current = false;
    setDraft(value);
    setEditing(true);
  }, [value]);

  const handleClick = useCallback(() => {
    startEdit();
  }, [startEdit]);

  const handleDisplayKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault();
        startEdit();
      }
    },
    [startEdit],
  );

  const handleTextareaChange = useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setDraft(e.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        commit(draft);
      } else if (e.key === "Escape") {
        e.preventDefault();
        cancelledRef.current = true;
        cancel();
      }
    },
    [draft, commit, cancel],
  );

  const handleBlur = useCallback(() => {
    if (cancelledRef.current) {
      return;
    }
    commit(draft);
  }, [draft, commit]);

  const rootStyle: React.CSSProperties = disabled
    ? { opacity: 0.5, pointerEvents: "none", cursor: "not-allowed" }
    : {};

  const fieldLabel = label ?? "Text block";
  const ariaLabel = editing
    ? "Editing " + fieldLabel + ". Press Ctrl+Enter to save, Escape to cancel."
    : fieldLabel + ". Press Enter to edit. Current value: " + (value || "empty");

  return (
    <div
      style={rootStyle}
      role="group"
      aria-label={fieldLabel}
      aria-disabled={disabled || undefined}
    >
      {label && (
        <div
          style={{
            font: "500 10px 'JetBrains Mono', monospace",
            letterSpacing: ".16em",
            textTransform: "uppercase",
            color: "var(--paper-ink-2)",
            marginBottom: "4px",
          }}
        >
          {label}
        </div>
      )}
      {editing ? (
        <textarea
          ref={textareaRef}
          rows={rows ?? 3}
          maxLength={maxLength}
          value={draft}
          onChange={handleTextareaChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          aria-label={ariaLabel}
          placeholder={placeholder}
          style={{
            font: "300 16px 'Fraunces', serif",
            padding: "4px 8px",
            outline: "2px solid var(--paper-gold)",
            background: "var(--color-input)",
            border: "1px solid var(--paper-rule)",
            color: "var(--paper-ink)",
            width: "100%",
            boxSizing: "border-box",
            resize: "vertical",
          }}
        />
      ) : (
        <div
          ref={displayRef}
          onClick={handleClick}
          onKeyDown={handleDisplayKeyDown}
          role="button"
          tabIndex={disabled ? -1 : 0}
          aria-label={ariaLabel}
          aria-disabled={disabled || undefined}
          style={{
            font: "300 16px 'Fraunces', serif",
            padding: "4px 8px",
            cursor: "text",
            color: value ? "var(--paper-ink)" : "var(--paper-ink-2)",
            userSelect: "none",
            whiteSpace: "pre-wrap",
            outline: "none",
          }}
        >
          {value || placeholder}
        </div>
      )}
    </div>
  );
}
